/**
 * ============================================================================
 * REPORT DATA SOURCE - Centralized data for all executive reports
 * ============================================================================
 * 
 * This file serves as the SINGLE SOURCE OF TRUTH for all report data.
 * When new data is imported into realFinancialData.js, it automatically
 * propagates to all reports through this module.
 * 
 * Structure:
 * - Import raw data from realFinancialData.js
 * - Calculate derived metrics (variations, margins, ratios)
 * - Export organized data objects for each report type
 * ============================================================================
 */

import {
  realIncomeStatementData,
  realCashFlowData,
  realBalanceSheetData,
} from './mock/realFinancialData';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Convert value to millions (M USD)
 */
export const toMillions = (value) => {
  if (!value && value !== 0) return 0;
  return Math.abs(value) / 1000000;
};

/**
 * Calculate percentage variation between two values
 */
export const calcVariation = (real, reference) => {
  if (!reference || reference === 0) return 0;
  return ((real - reference) / Math.abs(reference)) * 100;
};

/**
 * Format percentage with sign
 */
export const formatPercent = (value, decimals = 0) => {
  const formatted = value.toFixed(decimals);
  return value >= 0 ? `+${formatted}%` : `${formatted}%`;
};

/**
 * Format percentage points (pp)
 */
export const formatPP = (realMargin, refMargin) => {
  const diff = realMargin - refMargin;
  return diff >= 0 ? `+${diff.toFixed(1)}pp` : `${diff.toFixed(1)}pp`;
};

/**
 * Get safe value with fallback
 */
const safeValue = (val, fallback = 0) => (val !== undefined && val !== null ? val : fallback);

// ============================================================================
// PERIOD SELECTORS
// ============================================================================

/**
 * Get Income Statement data for a specific period
 * @param {string} period - '2024', '2025', '2026', 'Q1_2026', 'Q2_2026', etc.
 */
export const getIncomeStatementData = (period) => {
  const data = realIncomeStatementData;
  
  switch(period) {
    case '2024':
      return data['2024'];
    case '2025':
      return data['2025'];
    case '2026':
      return data['2026']?.annual;
    case 'Q1_2026':
    case '1T26':
      return data['2026']?.Q1;
    case 'Q2_2026':
    case '2T26':
      return data['2026']?.Q2;
    case 'Q3_2026':
    case '3T26':
      return data['2026']?.Q3;
    case 'Q4_2026':
    case '4T26':
      return data['2026']?.Q4;
    default:
      return data['2026']?.Q1; // Default to Q1 2026
  }
};

/**
 * Get Cash Flow data for a specific period
 */
export const getCashFlowData = (period) => {
  const data = realCashFlowData;
  
  switch(period) {
    case '2024':
      return data?.['2024'];
    case '2025':
      return data?.['2025'];
    case '2026':
      return data?.['2026']?.annual;
    case 'Q1_2026':
    case '1T26':
      return data?.['2026']?.Q1;
    case 'Q2_2026':
    case '2T26':
      return data?.['2026']?.Q2;
    default:
      return data?.['2026']?.Q1;
  }
};

/**
 * Get Balance Sheet data for a specific period
 */
export const getBalanceSheetData = (period) => {
  const data = realBalanceSheetData;
  
  switch(period) {
    case '2024':
      return data?.['2024'];
    case '2025':
      return data?.['2025'];
    case '2026':
      return data?.['2026'];
    default:
      return data?.['2026'];
  }
};

// ============================================================================
// PERFORMANCE FINANCEIRA REPORT DATA
// ============================================================================

/**
 * ADJUSTMENT FACTORS - Simulate Real vs Budget
 * These factors align with the dashboard logic in IncomeStatementMain.jsx
 * 
 * - 2025: Data is now REAL (no factor needed) - Updated with actual values
 * - 2026 Q1: Real = Budget * 0.95 (-5% variance from budget)
 * - 2024: No factor needed (actual data exists)
 * 
 * When real data is imported, remove/adjust these factors accordingly.
 */
const ADJUSTMENT_FACTORS = {
  '2026': {
    Q1: 0.95,  // Q1 2026: Real = Budget * 95% → -5% vs Budget
    Q2: 0.95,
    Q3: 0.95,
    Q4: 0.95,
    annual: 0.95,
  },
  '2025': 1.00, // 2025: REAL data now exists (no adjustment needed)
  '2024': 1.00, // 2024: Actual data exists, no adjustment
};

/**
 * Get adjustment factor for a period
 */
const getAdjustmentFactor = (period) => {
  if (period === 'Q1_2026' || period === '1T26') return ADJUSTMENT_FACTORS['2026'].Q1;
  if (period === 'Q2_2026' || period === '2T26') return ADJUSTMENT_FACTORS['2026'].Q2;
  if (period === 'Q3_2026' || period === '3T26') return ADJUSTMENT_FACTORS['2026'].Q3;
  if (period === 'Q4_2026' || period === '4T26') return ADJUSTMENT_FACTORS['2026'].Q4;
  if (period === '2025') return ADJUSTMENT_FACTORS['2025'];
  if (period === '2024') return ADJUSTMENT_FACTORS['2024'];
  return 1.0;
};

/**
 * Get complete data for Performance Financeira (DRE) Report
 * Used by: ReportPreview.jsx, IncomeStatementSlide1-5
 */
export const getPerformanceFinanceiraData = (period = 'Q1_2026') => {
  // Get budget data for current period
  const budgetData = getIncomeStatementData(period);
  
  // Get adjustment factor to simulate Real values
  const realFactor = getAdjustmentFactor(period);
  
  // Get previous year data for YoY comparison
  const prevYear = getIncomeStatementData('2025');
  // For Q1 2025: approximate from annual 2025 / 4
  const prevYearQ1Approx = prevYear ? {
    vendas: prevYear.vendas / 4,
    ebitda: prevYear.ebitda / 4,
    margemEbitda: prevYear.margemEbitda,
    resultadoLiquido: prevYear.resultadoLiquido / 4,
    totalProveitos: prevYear.totalProveitos / 4,
  } : null;

  if (!budgetData) {
    console.warn('No income statement data found for period:', period);
    return getDefaultPerformanceData();
  }

  // BUDGET values (raw from data file)
  const budgetVendas = toMillions(budgetData.vendas);
  const budgetEbitda = toMillions(budgetData.ebitda);
  const budgetMargemEbitda = (budgetData.margemEbitda || 0) * 100;
  const budgetNetIncome = toMillions(budgetData.resultadoLiquido);
  const budgetTotalProveitos = toMillions(budgetData.totalProveitos);

  // REAL values = Budget * adjustment factor
  const realVendas = budgetVendas * realFactor;
  const realEbitda = budgetEbitda * realFactor;
  // Margin is recalculated based on real values (approximately same percentage impact)
  const realMargemEbitda = budgetMargemEbitda; // Margin % stays similar
  const realNetIncome = budgetNetIncome * realFactor;
  const realTotalProveitos = budgetTotalProveitos * realFactor;

  // Previous year values (Q1 2025 approximation)
  const prevVendas = prevYearQ1Approx ? toMillions(prevYearQ1Approx.vendas) : 0;
  const prevEbitda = prevYearQ1Approx ? toMillions(prevYearQ1Approx.ebitda) : 0;
  const prevMargemEbitda = prevYearQ1Approx ? (prevYearQ1Approx.margemEbitda || 0) * 100 : 0;
  const prevNetIncome = prevYearQ1Approx ? toMillions(prevYearQ1Approx.resultadoLiquido) : 0;

  // Calculate variations vs Budget
  const vsOrcVendas = calcVariation(realVendas, budgetVendas);
  const vsOrcEbitda = calcVariation(realEbitda, budgetEbitda);
  const vsOrcMargin = 0; // Margin variance is minimal with proportional scaling
  const vsOrcNetIncome = calcVariation(realNetIncome, budgetNetIncome);

  // Calculate variations vs Previous Year (1T25)
  const vs1T25Vendas = calcVariation(realVendas, prevVendas);
  const vs1T25Ebitda = calcVariation(realEbitda, prevEbitda);
  const vs1T25Margin = realMargemEbitda - prevMargemEbitda;
  const vs1T25NetIncome = calcVariation(realNetIncome, prevNetIncome);

  return {
    // Absolute values (REAL = adjusted)
    revenue: realVendas,
    totalProveitos: realTotalProveitos,
    ebitda: realEbitda,
    ebitdaMargin: realMargemEbitda,
    netIncome: realNetIncome,
    isNetIncomeLoss: budgetData.resultadoLiquido < 0,
    
    // Budget values (raw)
    budgetRevenue: budgetVendas,
    budgetEbitda: budgetEbitda,
    budgetMargin: budgetMargemEbitda,
    budgetNetIncome: budgetNetIncome,
    
    // Previous year comparison
    prevRevenue: prevVendas,
    prevEbitda: prevEbitda,
    prevMargin: prevMargemEbitda,
    
    // Formatted variations vs Budget
    vsOrcRevenue: formatPercent(vsOrcVendas),
    vsOrcEbitda: formatPercent(vsOrcEbitda),
    vsOrcMargin: `${vsOrcMargin >= 0 ? '+' : ''}${vsOrcMargin.toFixed(1)}pp`,
    vsOrcNetIncome: formatPercent(vsOrcNetIncome),
    
    // Formatted variations vs Previous Year
    vs1T25Revenue: formatPercent(vs1T25Vendas),
    vs1T25Ebitda: formatPercent(vs1T25Ebitda),
    vs1T25Margin: `${vs1T25Margin >= 0 ? '+' : ''}${vs1T25Margin.toFixed(1)}pp`,
    vs1T25NetIncome: formatPercent(vs1T25NetIncome),
    
    // Raw variations (for conditional styling)
    vsOrcRevenueRaw: vsOrcVendas,
    vsOrcEbitdaRaw: vsOrcEbitda,
    vsOrcMarginRaw: vsOrcMargin,
    vs1T25RevenueRaw: vs1T25Vendas,
    vs1T25EbitdaRaw: vs1T25Ebitda,
    vs1T25MarginRaw: vs1T25Margin,
    
    // Detailed breakdown (using real/adjusted values)
    breakdown: {
      vendas: realVendas,
      outrosProveitos: toMillions(budgetData.outrosProveitos) * realFactor,
      variacaoPAePVF: toMillions(budgetData.variacaoPAePVF) * realFactor,
      cmvmc: toMillions(budgetData.cmvmc) * realFactor,
      custosPessoal: toMillions(budgetData.custosPessoal) * realFactor,
      subcontratos: toMillions(budgetData.subcontratos) * realFactor,
      geraisAdministrativos: toMillions(budgetData.geraisAdministrativos) * realFactor,
      custosComercializacao: toMillions(budgetData.custosComercializacao) * realFactor,
      amortizacoes: toMillions(budgetData.amortizacoes) * realFactor,
      ebit: toMillions(budgetData.ebit) * realFactor,
      resultadosFinanceiros: toMillions(budgetData.resultadosFinanceiros) * realFactor,
      impostoIndustrial: toMillions(budgetData.impostoIndustrial) * realFactor,
    },
    
    // Adjustment info
    adjustmentFactor: realFactor,
    isAdjusted: realFactor !== 1.0,
    
    // Period info
    period: period,
    periodLabel: period === 'Q1_2026' || period === '1T26' ? '1.º Trimestre 2026' : period,
  };
};

/**
 * Default data when no real data is available
 */
const getDefaultPerformanceData = () => ({
  revenue: 22.6,
  ebitda: 4.8,
  ebitdaMargin: 19.55,
  netIncome: -1.6,
  isNetIncomeLoss: true,
  vsOrcRevenue: '+0%',
  vsOrcEbitda: '+0%',
  vsOrcMargin: '+0.0pp',
  vsOrcNetIncome: '+0%',
  vs1T25Revenue: '-13%',
  vs1T25Ebitda: '-42%',
  vs1T25Margin: '-11.6pp',
  vs1T25NetIncome: '-172%',
  period: 'Q1_2026',
  periodLabel: '1.º Trimestre 2026',
});

// ============================================================================
// LIQUIDEZ E FLUXO DE CAIXA REPORT DATA
// ============================================================================

/**
 * Get complete data for Liquidez e Fluxo de Caixa Report
 * Used by: LiquidityReportPreview.jsx
 * 
 * Applies same adjustment factors as Performance Financeira for consistency
 */
export const getLiquidityReportData = (period = 'Q1_2026') => {
  const cashFlow = getCashFlowData(period);
  const balanceSheet = getBalanceSheetData('2026');
  const incomeStatement = getIncomeStatementData(period);
  
  // Get adjustment factor for consistency with DRE
  const realFactor = getAdjustmentFactor(period);
  
  // Get previous year for comparison
  const prevCashFlow = getCashFlowData('2025');
  const prevIncomeStatement = getIncomeStatementData('2025');
  
  if (!cashFlow || !incomeStatement) {
    console.warn('No cash flow or income statement data found for period:', period);
    return getDefaultLiquidityData();
  }

  // BUDGET values (raw)
  const budgetEbitda = toMillions(safeValue(incomeStatement.ebitda, 0));
  const budgetFco = toMillions(safeValue(cashFlow.fluxoCaixaOperacional, 0));
  
  // REAL values = Budget * adjustment factor
  const realEbitda = budgetEbitda * realFactor;
  const realFco = budgetFco * realFactor;
  
  // Cash flow breakdown (apply factor)
  const varCapitalGiro = toMillions(safeValue(cashFlow.variacaoCapitalGiro, 0)) * realFactor;
  const impostosPagos = toMillions(safeValue(cashFlow.impostosPagos, 0)) * realFactor;
  const juros = toMillions(safeValue(cashFlow.jurosPagos || cashFlow.jurosEEncargosFinanceiros, 0)) * realFactor;
  
  // Investment (apply factor)
  const capex = toMillions(safeValue(
    cashFlow.aquisicaoImobilizado || cashFlow.fornecedoresAtivosFixos, 0
  )) * realFactor;
  
  // Free Cash Flow
  const fcf = realFco - Math.abs(capex);
  
  // Balance sheet metrics
  const caixaDisponivel = toMillions(safeValue(balanceSheet?.caixaEquivalentes, 0));
  const dividaTotal = toMillions(safeValue(
    (balanceSheet?.emprestimosLongoPrazo || 0) + (balanceSheet?.emprestimosCurtoPrazo || 0), 0
  ));
  
  // Ratios
  const dividaLiquidaEbitda = realEbitda > 0 ? (dividaTotal - caixaDisponivel) / realEbitda : 0;
  const budgetDividaLiquidaEbitda = budgetEbitda > 0 ? (dividaTotal - caixaDisponivel) / budgetEbitda : 0;
  
  const liquidezCorrente = balanceSheet?.totalAtivoCorrente && balanceSheet?.totalPassivoCorrente 
    ? balanceSheet.totalAtivoCorrente / balanceSheet.totalPassivoCorrente 
    : 2.1;

  // Calculate variations vs Budget
  const vsFcoOrc = calcVariation(realFco, budgetFco);
  const vsLiquidezOrc = 0; // Liquidity ratio doesn't change significantly
  const vsDivLiqOrc = dividaLiquidaEbitda - budgetDividaLiquidaEbitda;

  // Previous year comparison (Q1 2025 approximation)
  const prevFco = prevCashFlow ? toMillions(safeValue(prevCashFlow.fluxoCaixaOperacional, 0)) / 4 : 8.2;
  const prevEbitda = prevIncomeStatement ? toMillions(safeValue(prevIncomeStatement.ebitda, 0)) / 4 : 8.2;
  const prevDivLiqEbitda = prevEbitda > 0 ? (dividaTotal - caixaDisponivel) / prevEbitda : 1.0;
  
  const vsFco1T25 = prevFco > 0 ? calcVariation(realFco, prevFco) : 0;
  const vsDivLiq1T25 = dividaLiquidaEbitda - prevDivLiqEbitda;

  return {
    // Cover Slide KPIs (REAL = adjusted)
    fco: realFco,
    fcoVsOrc: vsFcoOrc,
    fcoVs1T25: vsFco1T25,
    liquidezCorrente: liquidezCorrente,
    liquidezVsOrc: vsLiquidezOrc,
    caixaDisponivel: caixaDisponivel || 45.8,
    caixaVsOrc: calcVariation(caixaDisponivel, caixaDisponivel / realFactor), // Approximate
    divLiqEbitda: Math.abs(dividaLiquidaEbitda) || 0.8,
    divLiqVsOrc: vsDivLiqOrc,
    
    // Cash Flow Waterfall (Slide 2) - using REAL values
    cashFlowWaterfall: {
      ebitda: realEbitda,
      varCapitalGiro: -Math.abs(varCapitalGiro),
      impostosPagos: -Math.abs(impostosPagos),
      juros: -Math.abs(juros),
      outrosOp: 0,
      fco: realFco,
    },
    
    // Free Cash Flow Bridge
    freeCashFlowBridge: {
      fco: realFco,
      capex: -Math.abs(capex),
      fcf: fcf,
    },
    
    // Balance Sheet Metrics
    balanceMetrics: {
      caixaDisponivel: caixaDisponivel || 45.8,
      dividaTotal: dividaTotal,
      dividaLiquida: dividaTotal - caixaDisponivel,
      dividaLiquidaEbitda: Math.abs(dividaLiquidaEbitda) || 0.8,
      liquidezCorrente: liquidezCorrente,
    },
    
    // Budget values for reference
    budgetFco: budgetFco,
    budgetEbitda: budgetEbitda,
    
    // Adjustment info
    adjustmentFactor: realFactor,
    isAdjusted: realFactor !== 1.0,
    
    // Period info
    period: period,
    periodLabel: period === 'Q1_2026' || period === '1T26' ? '1.º Trimestre 2026' : period,
  };
};

/**
 * Default liquidity data when no real data is available
 */
const getDefaultLiquidityData = () => ({
  fco: 18.2,
  fcoVsOrc: 28,
  fcoVs1T25: 34,
  liquidezCorrente: 2.1,
  liquidezVsOrc: 0.3,
  caixaDisponivel: 45.8,
  caixaVsOrc: 12,
  divLiqEbitda: 0.8,
  divLiqVsOrc: -0.2,
  cashFlowWaterfall: {
    ebitda: 30.1,
    varCapitalGiro: -4.2,
    impostosPagos: -6.8,
    juros: -2.4,
    outrosOp: 1.5,
    fco: 18.2,
  },
  period: 'Q1_2026',
  periodLabel: '1.º Trimestre 2026',
});

// ============================================================================
// INCOME STATEMENT SLIDES DATA
// ============================================================================

/**
 * Get data for Income Statement Slide 2 (Formação do Resultado - Waterfalls)
 * Applies adjustment factors for Real vs Budget consistency
 */
export const getSlide2Data = (period = 'Q1_2026') => {
  const budgetData = getIncomeStatementData(period);
  const prevYear = getIncomeStatementData('2025');
  const realFactor = getAdjustmentFactor(period);
  
  if (!budgetData) return getDefaultSlide2Data();
  
  // Budget EBITDA (raw)
  const budgetEbitda = toMillions(budgetData.ebitda);
  // Real EBITDA = Budget * factor
  const realEbitda = budgetEbitda * realFactor;
  // Previous year Q1 approximation
  const prevEbitda = prevYear ? toMillions(prevYear.ebitda) / 4 : 8.2;
  
  // Calculate drivers for Real vs 1T25
  const ebitdaDiff = realEbitda - prevEbitda;
  
  // Calculate drivers for Real vs Budget
  const budgetDiff = realEbitda - budgetEbitda;
  
  return {
    // Previous year (Q1 2025)
    ebitda1T25: prevEbitda,
    // Real Q1 2026 (adjusted)
    ebitda1T26: realEbitda,
    // Budget Q1 2026 (raw)
    ebitdaOrc1T26: budgetEbitda,
    // Drivers explaining 1T25 → 1T26 Real
    drivers1T25to1T26: {
      volRegulares: ebitdaDiff * 0.35,
      volEspeciais: ebitdaDiff * 0.25,
      precoRegulares: ebitdaDiff * -0.15,
      precoEspeciais: ebitdaDiff * -0.10,
      efficiency: ebitdaDiff * 0.30,
      outros: ebitdaDiff * 0.35,
    },
    // Drivers explaining Budget → Real (all negative due to -5%)
    driversVsBudget: {
      volRegulares: budgetDiff * 0.30,
      volEspeciais: budgetDiff * 0.20,
      precoRegulares: budgetDiff * 0.15,
      precoEspeciais: budgetDiff * 0.10,
      efficiency: budgetDiff * 0.15,
      outros: budgetDiff * 0.10,
    },
    // Variation info
    vsOrcVariation: (realFactor - 1) * 100, // -5%
    vs1T25Variation: prevEbitda > 0 ? ((realEbitda / prevEbitda) - 1) * 100 : 0,
  };
};

const getDefaultSlide2Data = () => ({
  ebitda1T25: 8.2,
  ebitda1T26: 4.8,
  drivers1T25to1T26: {
    volRegulares: -1.2,
    volEspeciais: -0.8,
    precoRegulares: -0.6,
    precoEspeciais: -0.4,
    efficiency: -0.2,
    outros: -0.2,
  },
  ebitdaOrc1T26: 4.8,
  driversVsBudget: {
    volRegulares: 0,
    volEspeciais: 0,
    precoRegulares: 0,
    precoEspeciais: 0,
    efficiency: 0,
    outros: 0,
  },
});

/**
 * Get data for Income Statement Slide 3 (Formação da Margem)
 * Applies adjustment factors for consistency
 */
export const getSlide3Data = (period = 'Q1_2026') => {
  const budgetData = getIncomeStatementData(period);
  const prevYear = getIncomeStatementData('2025');
  const realFactor = getAdjustmentFactor(period);
  
  if (!budgetData) return getDefaultSlide3Data();
  
  // Margins (percentage doesn't change much with proportional scaling)
  const budgetMargin = (budgetData.margemEbitda || 0) * 100;
  const realMargin = budgetMargin; // Margin stays similar with proportional adjustment
  const prevMargin = prevYear ? (prevYear.margemEbitda || 0) * 100 : 31.18;
  const marginDiff = realMargin - prevMargin;
  
  return {
    // Previous year margin
    marginEbitda1T25: prevMargin,
    // Real Q1 2026 margin
    marginEbitda1T26: realMargin,
    // Budget margin
    marginEbitdaOrc1T26: budgetMargin,
    // Drivers explaining margin change
    drivers: {
      cmvmc: marginDiff * 0.3,
      pessoal: marginDiff * 0.2,
      ga: marginDiff * 0.1,
      comercializacao: marginDiff * 0.15,
      escala: marginDiff * 0.25,
    },
    // Variation vs previous year
    vs1T25Variation: marginDiff,
    // Variation vs budget (0 for margin with proportional scaling)
    vsOrcVariation: 0,
  };
};

const getDefaultSlide3Data = () => ({
  marginEbitda1T25: 31.18,
  marginEbitda1T26: 19.55,
  drivers: {
    cmvmc: -4.0,
    pessoal: -3.0,
    ga: -1.5,
    comercializacao: -2.0,
    escala: -1.13,
  },
});

/**
 * Get data for Income Statement Slide 4 (Conversão em Caixa)
 * Applies adjustment factors for consistency
 */
export const getSlide4Data = (period = 'Q1_2026') => {
  const budgetIncomeStatement = getIncomeStatementData(period);
  const budgetCashFlow = getCashFlowData(period);
  const realFactor = getAdjustmentFactor(period);
  
  if (!budgetIncomeStatement || !budgetCashFlow) return getDefaultSlide4Data();
  
  // Budget values (raw)
  const budgetEbitda = toMillions(budgetIncomeStatement.ebitda);
  const budgetFco = toMillions(safeValue(budgetCashFlow.fluxoCaixaOperacional, 0));
  const budgetCapex = toMillions(safeValue(budgetCashFlow.aquisicaoImobilizado || budgetCashFlow.fornecedoresAtivosFixos, 0));
  
  // Real values = Budget * factor
  const realEbitda = budgetEbitda * realFactor;
  const realFco = budgetFco * realFactor;
  const realCapex = budgetCapex * realFactor;
  const realWorkingCapital = toMillions(safeValue(budgetCashFlow.variacaoCapitalGiro, 0)) * realFactor;
  const realTaxes = toMillions(safeValue(budgetCashFlow.impostosPagos, 0)) * realFactor;
  
  return {
    // Real values (adjusted)
    ebitda: realEbitda,
    workingCapital: -Math.abs(realWorkingCapital),
    taxes: -Math.abs(realTaxes),
    otherAdjustments: 0,
    operatingCashFlow: realFco,
    capex: -Math.abs(realCapex),
    freeCashFlow: realFco - Math.abs(realCapex),
    
    // Budget values for comparison
    budgetEbitda: budgetEbitda,
    budgetFco: budgetFco,
    
    // Ratios
    netDebtEbitda: 0.8,
    debtCoverage: 3.8,
    availableCash: 45.8,
    
    // Variations
    vsOrcEbitda: (realFactor - 1) * 100, // -5%
    vsOrcFco: (realFactor - 1) * 100,     // -5%
  };
};

const getDefaultSlide4Data = () => ({
  ebitda: 4.8,
  workingCapital: -2.4,
  taxes: -1.8,
  otherAdjustments: -0.5,
  operatingCashFlow: 1.6,
  capex: -8.2,
  freeCashFlow: -6.6,
  netDebtEbitda: 0.8,
  debtCoverage: 3.8,
  availableCash: 45.8,
});

// ============================================================================
// EXPORT ALL DATA GETTERS
// ============================================================================

export default {
  // Utility functions
  toMillions,
  calcVariation,
  formatPercent,
  formatPP,
  
  // Period selectors
  getIncomeStatementData,
  getCashFlowData,
  getBalanceSheetData,
  
  // Report data getters
  getPerformanceFinanceiraData,
  getLiquidityReportData,
  
  // Slide-specific data
  getSlide2Data,
  getSlide3Data,
  getSlide4Data,
};
